import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Pill, Plus, Pencil, Trash2, X, Check } from "lucide-react";

const TIPO_USO_OPTIONS = [
  { value: "continuo", label: "Contínuo" },
  { value: "temporario", label: "Temporário" },
];

const STATUS_OPTIONS = [
  { value: "ativo", label: "Ativo" },
  { value: "suspenso", label: "Suspenso" },
];

const STATUS_BADGE = {
  ativo: "bg-emerald-100 text-emerald-800 border-emerald-200",
  suspenso: "bg-neutral-200 text-neutral-700 border-neutral-300",
};

const emptyForm = () => ({
  farmaco_codigo: "",
  dose: "",
  data_inicio: "",
  tipo_uso: "continuo",
  status: "ativo",
});

function labelFor(list, value) {
  return list.find((o) => o.value === value)?.label || value;
}

function MedicationForm({ drugs, initial, onCancel, onSaved, submitting }) {
  const [form, setForm] = useState(initial || emptyForm());
  const isEdit = !!initial;
  const prefix = isEdit ? "edit-medication" : "add-medication";

  const submit = () => {
    if (!form.farmaco_codigo) {
      toast.error("Selecione o fármaco.");
      return;
    }
    if (!form.dose.trim()) {
      toast.error("Informe a dose.");
      return;
    }
    onSaved({
      farmaco_codigo: form.farmaco_codigo,
      dose: form.dose.trim(),
      data_inicio: (form.data_inicio || "").trim(),
      tipo_uso: form.tipo_uso,
      status: form.status,
    });
  };

  return (
    <div
      className="rounded-sm border border-primary/30 bg-primary/5 p-4"
      data-testid={`${prefix}-form`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="overline text-muted-foreground mb-1 block">Fármaco</label>
          <select
            value={form.farmaco_codigo}
            onChange={(e) => setForm({ ...form, farmaco_codigo: e.target.value })}
            className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
            data-testid={`${prefix}-farmaco-select`}
          >
            <option value="">Selecione…</option>
            {drugs.map((d) => (
              <option key={d.codigo} value={d.codigo}>
                {d.codigo} — {d.nome}
                {d.classe_terapeutica ? ` (${d.classe_terapeutica})` : ""}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="overline text-muted-foreground mb-1 block">Dose</label>
          <input
            value={form.dose}
            onChange={(e) => setForm({ ...form, dose: e.target.value })}
            className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
            placeholder="Ex: 1000 mg"
            data-testid={`${prefix}-dose-input`}
          />
        </div>

        <div>
          <label className="overline text-muted-foreground mb-1 block">
            Data de início <span className="text-muted-foreground/70 lowercase">(opcional)</span>
          </label>
          <input
            value={form.data_inicio}
            onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
            className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
            placeholder="Ex: 03/2024"
            data-testid={`${prefix}-data-inicio-input`}
          />
        </div>

        <div>
          <label className="overline text-muted-foreground mb-1 block">Tipo de uso</label>
          <select
            value={form.tipo_uso}
            onChange={(e) => setForm({ ...form, tipo_uso: e.target.value })}
            className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
            data-testid={`${prefix}-tipo-uso-select`}
          >
            {TIPO_USO_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="overline text-muted-foreground mb-1 block">Status</label>
          <select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
            data-testid={`${prefix}-status-select`}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border text-xs hover:bg-muted"
          data-testid={`${prefix}-cancel-btn`}
        >
          <X className="w-3.5 h-3.5" /> Cancelar
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-60"
          data-testid={`${prefix}-save-btn`}
        >
          <Check className="w-3.5 h-3.5" /> {isEdit ? "Salvar" : "Adicionar"}
        </button>
      </div>
    </div>
  );
}

function MedicationRow({ med, onEdit, onDelete }) {
  const badgeCls = STATUS_BADGE[med.status] || STATUS_BADGE.suspenso;
  return (
    <div
      className="flex items-start justify-between gap-3 py-3 px-3 rounded-sm border border-border bg-card"
      data-testid={`medication-row-${med.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="text-sm font-semibold text-primary"
            data-testid={`medication-name-${med.id}`}
          >
            {med.farmaco_nome}
          </span>
          <span className="overline text-[10px] text-muted-foreground tracking-[0.18em]">
            {med.farmaco_codigo}
          </span>
          <span
            className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm border ${badgeCls}`}
            data-testid={`medication-status-${med.id}`}
          >
            {labelFor(STATUS_OPTIONS, med.status)}
          </span>
        </div>
        <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          <span data-testid={`medication-dose-${med.id}`}>
            <b className="text-foreground/80">Dose:</b> {med.dose || "—"}
          </span>
          <span>
            <b className="text-foreground/80">Uso:</b> {labelFor(TIPO_USO_OPTIONS, med.tipo_uso)}
          </span>
          {med.data_inicio ? (
            <span>
              <b className="text-foreground/80">Início:</b> {med.data_inicio}
            </span>
          ) : null}
          {med.farmaco_classe ? (
            <span className="text-muted-foreground/80">{med.farmaco_classe}</span>
          ) : null}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        <button
          onClick={() => onEdit(med)}
          className="p-1.5 rounded-sm border border-primary/30 text-primary hover:bg-primary/5"
          title="Editar"
          data-testid={`edit-medication-${med.id}`}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onDelete(med)}
          className="p-1.5 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5"
          title="Remover"
          data-testid={`delete-medication-${med.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function PatientMedicationsSection({ patientId }) {
  const [meds, setMeds] = useState([]);
  const [drugs, setDrugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, d] = await Promise.all([
        api.get(`/admin/patients/${patientId}/medications`),
        api.get("/admin/drugs"),
      ]);
      setMeds(m.data || []);
      setDrugs(d.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar medicações.");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [patientId]); // eslint-disable-line

  const create = async (payload) => {
    setSubmitting(true);
    try {
      await api.post(`/admin/patients/${patientId}/medications`, payload);
      toast.success("Medicação adicionada.");
      setCreating(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erro ao adicionar.");
    }
    setSubmitting(false);
  };

  const update = async (payload) => {
    setSubmitting(true);
    try {
      await api.patch(`/admin/patients/${patientId}/medications/${editing.id}`, payload);
      toast.success("Medicação atualizada.");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erro ao atualizar.");
    }
    setSubmitting(false);
  };

  const remove = async (med) => {
    if (!window.confirm(`Remover "${med.farmaco_nome}" da lista de medicações?`)) return;
    try {
      await api.delete(`/admin/patients/${patientId}/medications/${med.id}`);
      toast.success("Medicação removida.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erro ao remover.");
    }
  };

  return (
    <div
      className="bg-card border border-border rounded-sm p-6"
      data-testid="patient-medications-section"
    >
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="overline text-gold mb-2 inline-flex items-center gap-2">
            <Pill className="w-3.5 h-3.5" /> Bulário do paciente
          </div>
          <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">
            Medicações em uso
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Vincule fármacos do bulário a este paciente. Cruzamento com exames vem na próxima etapa.
          </p>
        </div>
        {!creating && !editing && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold hover:bg-primary/90"
            data-testid="add-medication-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Adicionar medicação
          </button>
        )}
      </div>

      {creating && (
        <div className="mb-4">
          <MedicationForm
            drugs={drugs}
            initial={null}
            submitting={submitting}
            onCancel={() => setCreating(false)}
            onSaved={create}
          />
        </div>
      )}

      {loading ? (
        <div className="py-6 text-center text-xs text-muted-foreground overline">Carregando…</div>
      ) : meds.length === 0 && !creating ? (
        <div
          className="py-6 text-center text-sm text-muted-foreground"
          data-testid="medications-empty"
        >
          Nenhuma medicação vinculada a este paciente.
        </div>
      ) : (
        <div className="space-y-2">
          {meds.map((m) =>
            editing?.id === m.id ? (
              <MedicationForm
                key={m.id}
                drugs={drugs}
                initial={{
                  farmaco_codigo: m.farmaco_codigo,
                  dose: m.dose,
                  data_inicio: m.data_inicio || "",
                  tipo_uso: m.tipo_uso,
                  status: m.status,
                }}
                submitting={submitting}
                onCancel={() => setEditing(null)}
                onSaved={update}
              />
            ) : (
              <MedicationRow key={m.id} med={m} onEdit={setEditing} onDelete={remove} />
            ),
          )}
        </div>
      )}
    </div>
  );
}
